import { useEffect, useRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import Loader from '../Loader';
import sty from './modal.module.scss';

// Intent glyphs (feather-style), coloured by the .icon.<intent> class. Kept
// inline so the colour tracks the intent token rather than a fixed asset.
const ICONS = {
  success: (
    <>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </>
  ),
  warning: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </>
  ),
  danger: (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
};

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Modal — design-system dialog shell. Overlay + centred rounded card with an
 * optional intent icon, heading, free-form body, and a right-aligned action
 * footer (ghost secondary + brand/danger primary). Handles Escape, backdrop
 * click, focus trap and focus restore so consumers don't re-implement them.
 *
 * `intent` (success|info|warning|danger) drives the icon colour, the primary
 * button colour (danger → red) and the initial focus (danger focuses Cancel).
 */
function Modal({
  title,
  children,
  intent,
  primaryAction,
  secondaryAction,
  onClose,
  closeOnBackdrop = true,
  ariaLabel,
  width,
  center = true,
  bare = false,
  className,
}) {
  const dialogRef = useRef(null);
  const restoreFocusRef = useRef(null);

  // Move focus in on open (Cancel for danger, primary otherwise); restore it on
  // close so keyboard users land back where they were.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement;
    const node = dialogRef.current;
    if (node) {
      const focusables = node.querySelectorAll(FOCUSABLE);
      const initial = intent === 'danger' ? focusables[0] : focusables[focusables.length - 1];
      (initial || node).focus?.();
    }
    return () => restoreFocusRef.current?.focus?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape to dismiss + trap Tab within the dialog.
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape' && onClose) {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE)).filter(
        el => !el.disabled,
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className={className}>
      {primaryAction?.loading && <Loader />}
      <div
        className={sty.overlay}
        role="presentation"
        onClick={closeOnBackdrop && onClose ? onClose : undefined}
      />
      <div
        ref={dialogRef}
        className={cx(sty.dialog, { [sty.center]: center })}
        style={width ? { width } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : undefined)}
        tabIndex={-1}
      >
        <div className={cx(sty.card, { [sty.bare]: bare })}>
          {bare ? (
            children
          ) : (
            <>
              {intent && ICONS[intent] && (
                <div className={cx(sty.icon, sty[intent])}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    {ICONS[intent]}
                  </svg>
                </div>
              )}
              {title && <h2 className={sty.title}>{title}</h2>}
              <div className={sty.body}>{children}</div>
              {(primaryAction || secondaryAction) && (
                <div className={sty.footer}>
                  {secondaryAction && (
                    <button
                      type="button"
                      className={sty.secondary}
                      onClick={secondaryAction.onClick}
                    >
                      {secondaryAction.label}
                    </button>
                  )}
                  {primaryAction && (
                    <button
                      type="button"
                      className={cx(sty.primary, { [sty.danger]: intent === 'danger' })}
                      onClick={primaryAction.onClick}
                      disabled={primaryAction.disabled || primaryAction.loading}
                    >
                      {primaryAction.label}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

Modal.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
  intent: PropTypes.oneOf(['success', 'info', 'warning', 'danger']),
  primaryAction: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
  }),
  secondaryAction: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  onClose: PropTypes.func,
  closeOnBackdrop: PropTypes.bool,
  ariaLabel: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  center: PropTypes.bool,
  bare: PropTypes.bool,
  className: PropTypes.string,
};

export default Modal;
